import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { RequirePermission, SETTINGS_MANAGE_PERMISSION } from '../../lib/auth';
import type { RefundPolicy, RefundPolicyType } from '../../lib/types';
import {
  Button,
  Card,
  FormField,
  HelpPanel,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui';

/**
 * Dashboard brief task 3 ("Refund policy editor"). Backed by `GET`/`PATCH /dashboard/refund-
 * policy` - RECONCILED mid-task against the backend agent's own in-progress work on
 * `packages/db/src/schema/tenants.ts` (found uncommitted in this working tree), whose doc comment
 * on its new `RefundPolicy` type says this exact route is "Read/written today only by
 * apps/client-api's tenant-Owner route" - see lib/api.ts's `refundPolicy` client and
 * lib/types.ts's `RefundPolicy` doc comment for exactly what's confirmed vs. still assumed (the
 * route PATH/BODY SHAPE are reconciled against that real schema type; whether the route handler
 * itself is wired up and live was not confirmed - it did not exist under
 * apps/client-api/src/routes at the time this was written).
 *
 * "Editable by the Owner role only" (brief) is gated with `SETTINGS_MANAGE_PERMISSION` - see
 * lib/auth.tsx's doc comment on that constant. That schema file's own "tenant-Owner route" wording
 * independently confirms this Owner-only gating decision, even though the exact permission name
 * enforced server-side is still unknown.
 */
export function RefundPolicyPage() {
  return (
    <div>
      <PageHeader
        title="Refund policy"
        description="What buyers are told about refunds when they ask for one, and what your staff should do."
      />
      <HelpPanel storageKey="help-refund-policy" title="What is this?">
        This is the refund rule shown to your buyers and used by your staff when someone asks for
        a refund. Choose the option that matches how you actually run refunds, then write a short
        plain-English sentence describing it - that sentence is what buyers actually see.
      </HelpPanel>
      <RefundPolicyEditor />
    </div>
  );
}

const POLICY_TYPE_LABELS: Record<RefundPolicyType, string> = {
  none: 'No refunds',
  full_until_x_days: 'Full refund until a number of days before the event',
  only_if_cancelled: 'Only refund if we cancel the event',
  custom: 'Custom',
};

const POLICY_TYPE_HINTS: Record<RefundPolicyType, string> = {
  none: 'Buyers are told that all sales are final and no refunds are offered.',
  full_until_x_days:
    'Buyers get a full refund if they ask before the cut-off below; after that, no refund.',
  only_if_cancelled: 'Buyers are only refunded if YOU cancel or reschedule the event.',
  custom: 'Pick this when none of the above describe your policy - write your own rule below.',
};

const DEFAULT_POLICY_TEXT: Record<RefundPolicyType, (days: string) => string> = {
  none: () => 'All sales are final. We do not offer refunds.',
  full_until_x_days: (days) =>
    `Full refunds are available if you request one at least ${days} days before the event.`,
  only_if_cancelled: () => 'Refunds are only issued if we cancel or reschedule the event.',
  custom: () => '',
};

function RefundPolicyEditor() {
  const queryClient = useQueryClient();
  const policyQuery = useQuery({ queryKey: ['refund-policy'], queryFn: api.refundPolicy.get });

  const [policyType, setPolicyType] = useState<RefundPolicyType>('none');
  const [daysBeforeEvent, setDaysBeforeEvent] = useState('7');
  const [policyText, setPolicyText] = useState(DEFAULT_POLICY_TEXT.none(''));
  const [textTouched, setTextTouched] = useState(false);
  const [formError, setFormError] = useState<ParsedApiError | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the form from the loaded policy exactly once it arrives - mirrors the stale-initial-
  // state fix documented in features/events/EventDetailPage.tsx (useState's initializer only
  // runs on first mount, and this component is mounted before the query resolves).
  useEffect(() => {
    const policy = policyQuery.data;
    if (!policy) return;
    setPolicyType(policy.policyType);
    if (policy.daysBeforeEvent != null) setDaysBeforeEvent(String(policy.daysBeforeEvent));
    setPolicyText(policy.policyText);
    setTextTouched(true);
  }, [policyQuery.data]);

  function handlePolicyTypeChange(next: RefundPolicyType) {
    setPolicyType(next);
    // Offer a sensible default sentence for the structured policy types so a non-technical owner
    // isn't left staring at a required text field with no idea what to write - but never
    // overwrite text they've already typed/edited themselves.
    if (!textTouched) setPolicyText(DEFAULT_POLICY_TEXT[next](daysBeforeEvent));
  }

  const mutation = useMutation({
    mutationFn: () => {
      const body: RefundPolicy = {
        policyType,
        policyText: policyText.trim(),
        ...(policyType === 'full_until_x_days' ? { daysBeforeEvent: Number(daysBeforeEvent) || 0 } : {}),
      };
      return api.refundPolicy.update(body);
    },
    onSuccess: () => {
      setFormError(null);
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['refund-policy'] });
    },
    onError: (err) => {
      setSaved(false);
      setFormError(toParsedError(err));
    },
  });

  if (policyQuery.isLoading) return <Spinner />;
  if (policyQuery.error) {
    return <ErrorSummary error={toParsedError(policyQuery.error)} />;
  }

  return (
    <Card title="Your refund policy">
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      {saved && !formError && (
        <p className="mb-3 text-sm text-emerald-700">Refund policy saved.</p>
      )}
      <RequirePermission
        permission={SETTINGS_MANAGE_PERMISSION}
        fallback={
          <>
            <p className="text-sm text-slate-500 mb-1">
              Only the account Owner can change the refund policy. Current policy:{' '}
              <strong>{POLICY_TYPE_LABELS[policyType]}</strong>
            </p>
            {policyText && <p className="text-sm text-slate-600">"{policyText}"</p>}
          </>
        }
      >
        <FormField label="Policy type" hint={POLICY_TYPE_HINTS[policyType]}>
          <Select
            value={policyType}
            onChange={(e) => handlePolicyTypeChange(e.target.value as RefundPolicyType)}
          >
            {Object.entries(POLICY_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormField>
        {policyType === 'full_until_x_days' && (
          <FormField
            label="Days before the event"
            hint="A buyer who asks for a refund this many days (or more) before the event gets a full refund."
          >
            <Input
              type="number"
              min={0}
              value={daysBeforeEvent}
              onChange={(e) => {
                setDaysBeforeEvent(e.target.value);
                if (!textTouched) {
                  setPolicyText(DEFAULT_POLICY_TEXT.full_until_x_days(e.target.value));
                }
              }}
            />
          </FormField>
        )}
        <FormField
          label="Policy text"
          hint="Shown to buyers exactly as typed - this is the sentence they actually read."
        >
          <Textarea
            rows={3}
            value={policyText}
            onChange={(e) => {
              setPolicyText(e.target.value);
              setTextTouched(true);
            }}
          />
        </FormField>
        <Button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || !policyText.trim()}
        >
          {mutation.isPending ? 'Saving...' : 'Save refund policy'}
        </Button>
      </RequirePermission>
    </Card>
  );
}
